import type { ComponentType } from 'react';
import { ChatCircleDots, Clipboard, Code, Handshake, Phone, UsersThree, VideoCamera } from '@phosphor-icons/react';

type StageIconComponent = ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;

/**
 * TEC-512 editorial-rail redesign: icon per recruitment-process stage, same free-text-facet
 * pattern as skill-icons.tsx (LLM-extracted stage labels, not a fixed enum); curated exact
 * matches first, then a keyword bucket, since a hiring pipeline's actual step names vary a lot
 * ("Screening call" vs "Intro call" vs "Recruiter screen" all mean the same stage).
 */
const STAGE_ICONS: Record<string, StageIconComponent> = {
  'screening call': Phone,
  'phone screen': Phone,
  'phone interview': Phone,
  'intro call': Phone,
  'recruiter call': Phone,
  'recruiter screen': Phone,
  'initial call': Phone,
  'video interview': VideoCamera,
  'video call': VideoCamera,
  'technical interview': Code,
  'technical assessment': Code,
  'technical screen': Code,
  'coding test': Code,
  'coding challenge': Code,
  'take-home assignment': Code,
  'take home assignment': Code,
  'case study': Code,
  'technical round': Code,
  'meet the team': UsersThree,
  onsite: UsersThree,
  'team interview': UsersThree,
  'panel interview': UsersThree,
  'culture fit': UsersThree,
  'hiring manager interview': ChatCircleDots,
  interview: ChatCircleDots,
  'reference check': Clipboard,
  'background check': Clipboard,
  offer: Handshake,
  'offer stage': Handshake,
  'job offer': Handshake,
};

const CALL_KEYWORDS = ['screen', 'phone', 'recruiter', 'intro call'];
const VIDEO_KEYWORDS = ['video'];
const TECHNICAL_KEYWORDS = ['technical', 'coding', 'assessment', 'take-home', 'take home', 'case study', 'challenge'];
const TEAM_KEYWORDS = ['team', 'onsite', 'culture', 'panel'];
const INTERVIEW_KEYWORDS = ['interview', 'meet'];
const CHECK_KEYWORDS = ['reference', 'background check'];
const OFFER_KEYWORDS = ['offer'];

function matchesAny(value: string, keywords: string[]): boolean {
  return keywords.some((keyword) => value.includes(keyword));
}

/**
 * Resolves a recruitment-process step label to an icon: exact curated match first, then a
 * keyword bucket. Falls back to null (the caller keeps its plain numbered/dot marker) only for a
 * genuinely unrecognized step label.
 */
export function getRecruitmentStageIcon(value: string): StageIconComponent | null {
  const lower = value.toLowerCase().trim();
  const exact = STAGE_ICONS[lower];
  if (exact) return exact;

  if (matchesAny(lower, VIDEO_KEYWORDS)) return VideoCamera;
  if (matchesAny(lower, TECHNICAL_KEYWORDS)) return Code;
  if (matchesAny(lower, TEAM_KEYWORDS)) return UsersThree;
  if (matchesAny(lower, CHECK_KEYWORDS)) return Clipboard;
  if (matchesAny(lower, OFFER_KEYWORDS)) return Handshake;
  if (matchesAny(lower, CALL_KEYWORDS)) return Phone;
  if (matchesAny(lower, INTERVIEW_KEYWORDS)) return ChatCircleDots;

  return null;
}
